import * as React from 'react';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import {
	Button,
	Card,
	Col,
	Form,
	Input,
	Popover,
	Checkbox,
	Row,
	Select,
	TimePicker,
	message,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { TimeRangePickerWith24Hours } from './TimePicker24Hour';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	locationsRestCrudThunksForBuyer,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import { connect } from 'react-redux';
import { withUserType } from '../withUserType';
import {
	contractorLocationsRestCrudThunksForBuyer,
	contractorLocationsRestCrudThunksForSupplier,
} from '../../thunks/contractor_locations_thunks';
import { CopyOutlined } from '@ant-design/icons';
import { isBuyerOrInternalTech } from '../../utils/AuthUtils';
import { supplierFacilitiesRestCrudThunksForSupplier } from '../../thunks/supplier_facilities_thunks';

require('./OperatingHoursCardDisplay.less');

const DAYS_OF_THE_WEEK = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
];

const OperatingHoursCardDisplay: React.FC<any> = ({
	operatingHours = {},
	item,
	updateLocation,
	updateContractorLocation,
	updateSupplier,
	isContractor = false,
	canUpdate = true,
	isSupplier = false,
}) => {
	const [form] = Form.useForm();
	const [editMode, setEditMode] = useState(false);
	const isUpdate = useMemo(() => Object.keys(operatingHours).length !== 0, [operatingHours]);
	const [openKeys, setOpenKeys] = useState(Object.keys(operatingHours));
	const [submitLoading, setSubmitLoading] = useState(false);

	const daysOfWeekTable = (
		<table>
			{DAYS_OF_THE_WEEK.map((dayOfWeek, idx) => {
				const fromTime = nullSafeGet(`${dayOfWeek}.from`, operatingHours);
				const toTime = nullSafeGet(`${dayOfWeek}.to`, operatingHours);
				const hasHours = fromTime && toTime;
				const isAllDay =
					fromTime &&
					toTime &&
					moment(toTime, 'h:mm a').diff(moment(fromTime, 'h:mm a'), 'minutes') >= 1440 - 16; //15 min increments

				return (
					<tr key={idx} style={{ lineHeight: '32px' }}>
						<td>{dayOfWeek}</td>
						<td style={{ paddingLeft: 32, color: 'rgba(0,0,0,0.45)' }}>
							{hasHours ? (isAllDay ? 'All day (24 hours)' : `${fromTime} to ${toTime}`) : 'Closed'}
						</td>
					</tr>
				);
			})}
		</table>
	);

	useEffect(() => {
		if (editMode) {
			const initialValues = DAYS_OF_THE_WEEK.reduce((acc, day) => {
				const from = nullSafeGet(`${day}.from`, operatingHours)
					? moment(nullSafeGet(`${day}.from`, operatingHours), 'h:mm a')
					: undefined;
				const to = nullSafeGet(`${day}.to`, operatingHours)
					? moment(nullSafeGet(`${day}.to`, operatingHours), 'h:mm a')
					: undefined;
				acc[day] = { from, to };
				return acc;
			}, {});

			form.setFieldsValue(initialValues);
			setOpenKeys(Object.keys(operatingHours)); // also reset which days are open
		}
	}, [editMode]);

	const handleSubmit = (values) => {
		setSubmitLoading(true);
		const operatingHours = DAYS_OF_THE_WEEK.filter((day) => openKeys.includes(day)).reduce(
			(acc, day) => {
				const from = nullSafeGet(`${day}.from`, values);
				const to = nullSafeGet(`${day}.to`, values);

				if (from && to && moment.isMoment(from) && moment.isMoment(to)) {
					acc[day] = {
						from: from.format('h:mm a'),
						to: to.format('h:mm a'),
					};
				}
				return acc;
			},
			{}
		);

		const fn = isSupplier
			? updateSupplier
			: isContractor
			? updateContractorLocation
			: updateLocation;

		fn(item.id, operatingHours)
			.catch(() => {
				message.error('Error updating operating hours.');
			})
			.finally(() => {
				setSubmitLoading(false);
				setEditMode(false);
			});
	};

	const openDay = (dayOfWeek) => {
		setOpenKeys((prev) => (prev.includes(dayOfWeek) ? prev : [...prev, dayOfWeek]));
	};

	const closeDay = (dayOfWeek) => {
		setOpenKeys((prev) => prev.filter((key) => key !== dayOfWeek));
	};

	const CopyToDayContent = ({ today }) => {
		const [copyKeys, setCopyKeys] = useState([]);

		const handleCopyToSelected = (sourceDay) => {
			const src = form.getFieldValue(sourceDay);
			if (!nullSafeGet('from', src) || !nullSafeGet('to', src)) return;

			copyKeys.forEach((day) => {
				openDay(day);
				form.setFieldsValue({
					[day]: { from: src.from, to: src.to },
				});
			});

			setCopyKeys([]);
		};

		return (
			<div onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
				<div style={{ marginBottom: 8, fontWeight: 500 }}>Copy {today}’s hours to:</div>
				<Checkbox.Group
					value={copyKeys}
					onChange={(vals) => setCopyKeys(vals)}
					style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}
				>
					{DAYS_OF_THE_WEEK.filter((d) => d !== today).map((d) => (
						<Checkbox key={d} value={d} style={{ width: '100%', margin: 0 }}>
							{d}
						</Checkbox>
					))}
				</Checkbox.Group>
				<Button
					type="primary"
					size="small"
					block
					disabled={copyKeys.length === 0}
					style={{ marginTop: 8 }}
					onClick={() => handleCopyToSelected(today)}
				>
					Copy
				</Button>
			</div>
		);
	};

	const daysOfWeekForm = useMemo(
		() => (
			<Form form={form} onFinish={handleSubmit}>
				{DAYS_OF_THE_WEEK.map((dayOfWeek, idx) => {
					const from = nullSafeGet(`${dayOfWeek}.from`, operatingHours)
						? moment(nullSafeGet(`${dayOfWeek}.from`, operatingHours), 'h:mm a')
						: undefined;
					const to = nullSafeGet(`${dayOfWeek}.to`, operatingHours)
						? moment(nullSafeGet(`${dayOfWeek}.to`, operatingHours), 'h:mm a')
						: undefined;

					const isActive = openKeys.includes(dayOfWeek);

					return (
						<Row style={{ alignItems: 'center', minHeight: 40, marginBottom: 8, gap: 8 }}>
							<Col flex="none">
								<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
									<span style={{ minWidth: 75 }}>{dayOfWeek}</span>
									<span style={{ color: isActive ? 'green' : 'gray', minWidth: 45 }}>
										{isActive ? 'Open' : 'Closed'}
									</span>
								</div>
							</Col>
							<Col flex="auto">
								<div
									style={{
										display: 'flex',
										flexWrap: 'wrap',
										alignItems: 'center',
										gap: 4,
									}}
								>
									<Form.Item
										name={`${dayOfWeek}`}
										initialValue={{ from, to }}
										hidden={!isActive}
										style={{
											marginBottom: 0,
											maxWidth: 220,
											flex: '0 1 auto',
										}}
										rules={[
											{
												validator: async (_, value) => {
													// only validate if the day is open
													if (isActive) {
														if (!value.from || !value.to) {
															throw new Error(`Both start and end times required.`);
														}
													}
												},
											},
										]}
									>
										<TimeRangePickerWith24Hours
											use12Hours
											name={dayOfWeek}
											format="h:mm a"
											minuteStep={15}
											showNow={false}
										/>
									</Form.Item>

									<div style={{ display: 'flex', gap: 2 }}>
										{isActive ? (
											<>
												<Button
													className="rowButton"
													type="text"
													onClick={() => closeDay(dayOfWeek)}
												>
													<i className="icons8-font icons8-trash-can" />
												</Button>
												<Popover
													placement="bottomRight"
													content={<CopyToDayContent today={dayOfWeek} />}
												>
													<Button className="rowButton" type="text">
														<CopyOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
													</Button>
												</Popover>
											</>
										) : (
											<Button className="rowButton" type="text" onClick={() => openDay(dayOfWeek)}>
												<i className="icons8-font icons8-plus" />
											</Button>
										)}
									</div>
								</div>
							</Col>
						</Row>
					);
				})}
				<Row style={{ display: 'flex', gap: 8 }}>
					<Button onClick={form.submit} loading={submitLoading} type={'primary'}>
						Submit
					</Button>
					<Button onClick={() => setEditMode(false)}>Cancel</Button>
				</Row>
			</Form>
		),
		[operatingHours, openKeys, form, submitLoading]
	);

	return (
		<Card
			className={'OperatingHoursCardDisplay'}
			title="Business Hours"
			extra={
				editMode ? null : canUpdate ? (
					<Button type="link" onClick={() => setEditMode(true)}>
						{isUpdate ? 'Edit' : 'Add'}
					</Button>
				) : null
			}
		>
			{editMode ? daysOfWeekForm : isUpdate ? daysOfWeekTable : 'No hours selected.'}
		</Card>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateLocation: (id, operatingHours) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.patch(id, { operatingHours })
				: locationsRestCrudThunksForBuyer.patch(id, { operatingHours })
		),
	updateContractorLocation: (id, operatingHours) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorLocationsRestCrudThunksForSupplier.patch(id, { operatingHours })
				: contractorLocationsRestCrudThunksForBuyer.patch(id, { operatingHours })
		),
	updateSupplier: (id, operatingHours) =>
		dispatch(supplierFacilitiesRestCrudThunksForSupplier.patch(id, { operatingHours })),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(OperatingHoursCardDisplay)
);
